/**
 * Typed client for the RUNNER read API (apps/api). Every response is validated against the shared zod schemas in
 * @runner/schema, so a drifted or partial response becomes an explicit error instead of silently wrong numbers.
 * Read-only: no endpoint here signs, proposes or broadcasts anything.
 */
import {
  AccountAchievementsResponse,
  AccountRunnersResponse,
  ConfigResponse,
  DeploymentResponse,
  IdentitiesResponse,
  IdentityResponse,
  ManifestValidateResponse,
  OperatorNonceResponse,
  OperatorSessionResponse,
  SimulateConfigResponse,
  SimulateWaterfallResponse,
  VersionsResponse,
  EventsResponse,
  GameStateDto,
  HealthDto,
  JournalResponse,
  MarketResponse,
  QuoteResponse,
  ReplayResponse,
  RewardsAccountDto,
  RewardsBreakdownResponse,
  ShoeResponse,
  StepsResponse,
  TreasuryResponse,
  BuybackResponse,
  WindowResponse,
} from '@runner/schema';

/** Structural view of a zod schema (avoids a direct zod dependency in the web app). */
interface Parser<T> {
  safeParse(data: unknown): { success: true; data: T } | { success: false; error: { issues: { path: (string | number)[]; message: string }[] } };
}
export type Out<S> = S extends Parser<infer T> ? T : never;

export type DeploymentRes = Out<typeof DeploymentResponse>;
export type StateRes = Out<typeof GameStateDto>;
export type StepsRes = Out<typeof StepsResponse>;
export type QuoteRes = Out<typeof QuoteResponse>;
export type TreasuryRes = Out<typeof TreasuryResponse>;
export type RewardsRes = Out<typeof RewardsAccountDto>;
export type RewardsBreakdownRes = Out<typeof RewardsBreakdownResponse>;
export type ManifestValidateRes = Out<typeof ManifestValidateResponse>;
export type JournalRes = Out<typeof JournalResponse>;
export type EventsRes = Out<typeof EventsResponse>;
export type ReplayRes = Out<typeof ReplayResponse>;
export type MarketRes = Out<typeof MarketResponse>;
export type BuybackRes = Out<typeof BuybackResponse>;
export type ShoeRes = Out<typeof ShoeResponse>;
export type WindowRes = Out<typeof WindowResponse>;
export type HealthRes = Out<typeof HealthDto>;
export type VersionsRes = Out<typeof VersionsResponse>;
export type IdentitiesRes = Out<typeof IdentitiesResponse>;
export type IdentityRes = Out<typeof IdentityResponse>;
export type AccountRunnersRes = Out<typeof AccountRunnersResponse>;
export type AchievementsRes = Out<typeof AccountAchievementsResponse>;
export type ConfigRes = Out<typeof ConfigResponse>;
export type SimulateWaterfallRes = Out<typeof SimulateWaterfallResponse>;
export type SimulateConfigRes = Out<typeof SimulateConfigResponse>;
export type SourceDto = StateRes['source'];

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** Network-level failure (API process down, CORS, timeout) rather than an HTTP error response. */
  get unreachable(): boolean {
    return this.status === 0 && this.code !== 'schema';
  }
}

export class RunnerApiClient {
  constructor(
    readonly base: string,
    private readonly timeoutMs = 6000,
  ) {}

  private async get<T>(path: string, schema: Parser<T>, signal?: AbortSignal, init?: { method: 'POST'; body: unknown; token?: string }): Promise<T> {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), init ? Math.max(this.timeoutMs, 30_000) : this.timeoutMs);
    const onAbort = () => ctl.abort();
    signal?.addEventListener('abort', onAbort);
    let res: Response;
    try {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (init) headers['Content-Type'] = 'application/json';
      if (init?.token) headers.Authorization = `Bearer ${init.token}`;
      res = await fetch(`${this.base}${path}`, {
        signal: ctl.signal,
        cache: 'no-store',
        headers,
        ...(init ? { method: 'POST', body: JSON.stringify(init.body) } : {}),
      });
    } catch (e) {
      throw new ApiError(0, ctl.signal.aborted ? 'timeout' : 'unreachable', `API unreachable at ${this.base} (${(e as Error).message})`);
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    }
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      throw new ApiError(res.status, 'bad-body', `API ${path} returned a non-JSON body (HTTP ${res.status})`);
    }
    if (!res.ok) {
      const err = (body as { error?: { code?: string; message?: string }; reason?: string })?.error;
      // Preview endpoints answer 422 with a full body (valid: false, reason): pass it through to the caller.
      if (res.status === 422 && (body as { previewOnly?: boolean })?.previewOnly) {
        const parsed = schema.safeParse(body);
        if (parsed.success) return parsed.data;
      }
      throw new ApiError(res.status, err?.code ?? 'http', err?.message ?? `HTTP ${res.status}`);
    }
    const parsed = schema.safeParse(body);
    if (!parsed.success) {
      const i = parsed.error.issues[0];
      throw new ApiError(0, 'schema', `API ${path} response failed validation at ${i?.path.join('.') || '(root)'}: ${i?.message ?? 'invalid'}`);
    }
    return parsed.data;
  }

  health(signal?: AbortSignal) {
    return this.get('/v1/health', HealthDto, signal);
  }
  deployment() {
    return this.get('/v1/deployment', DeploymentResponse);
  }
  state(steps = 100) {
    return this.get(`/v1/state?steps=${steps}`, GameStateDto);
  }
  steps(runId: number, from = 0, limit = 200) {
    return this.get(`/v1/runs/${runId}/steps?from=${from}&limit=${limit}`, StepsResponse);
  }
  window(w: number) {
    return this.get(`/v1/windows/${w}`, WindowResponse);
  }
  market() {
    return this.get('/v1/market', MarketResponse);
  }
  quote(side: 'buy' | 'sell', amount: bigint, exactInput = true) {
    return this.get(`/v1/quote?side=${side}&exactInput=${exactInput}&amount=${amount.toString()}`, QuoteResponse);
  }
  treasury() {
    return this.get('/v1/treasury', TreasuryResponse);
  }
  buyback() {
    return this.get('/v1/buyback', BuybackResponse);
  }
  rewards(account: string) {
    return this.get(`/v1/rewards/${account}`, RewardsAccountDto);
  }
  rewardsBreakdown(account: string, limit = 100) {
    return this.get(`/v1/rewards/${account}/breakdown?limit=${limit}`, RewardsBreakdownResponse);
  }
  shoe(id: string | number, offset = 0, limit = 200) {
    return this.get(`/v1/prizes/shoes/${id}?offset=${offset}&limit=${limit}`, ShoeResponse);
  }
  journal(limit = 40) {
    return this.get(`/v1/journal?limit=${limit}`, JournalResponse);
  }
  events(after: number, limit = 200) {
    return this.get(`/v1/events?after=${after}&limit=${limit}`, EventsResponse);
  }
  verifyRun(id: number) {
    return this.get(`/v1/verify/run/${id}`, ReplayResponse);
  }
  versions() {
    return this.get('/v1/versions', VersionsResponse);
  }
  config(version?: number) {
    return this.get(`/v1/config${version === undefined ? '' : `?version=${version}`}`, ConfigResponse);
  }
  identities(limit = 30) {
    return this.get(`/v1/identities?limit=${limit}`, IdentitiesResponse);
  }
  identity(id: bigint | number | string) {
    return this.get(`/v1/identities/${id.toString()}`, IdentityResponse);
  }
  accountRunners(account: string) {
    return this.get(`/v1/accounts/${account}/runners`, AccountRunnersResponse);
  }
  achievements(account: string, limit = 50) {
    return this.get(`/v1/accounts/${account}/achievements?limit=${limit}`, AccountAchievementsResponse);
  }
  // Operator (preview only; nothing is signed or broadcast by the API).
  operatorNonce(address: string) {
    return this.get('/v1/operator/nonce', OperatorNonceResponse, undefined, { method: 'POST', body: { address } });
  }
  operatorSession(message: string, signature: string) {
    return this.get('/v1/operator/session', OperatorSessionResponse, undefined, { method: 'POST', body: { message, signature } });
  }
  simulateWaterfall(token: string, body: unknown) {
    return this.get('/v1/operator/simulate-waterfall', SimulateWaterfallResponse, undefined, { method: 'POST', body, token });
  }
  simulateConfig(token: string, body: unknown) {
    return this.get('/v1/operator/simulate-config', SimulateConfigResponse, undefined, { method: 'POST', body, token });
  }
  validateManifest(token: string, manifest: unknown) {
    return this.get('/v1/operator/manifest/validate', ManifestValidateResponse, undefined, { method: 'POST', body: { manifest }, token });
  }
}
